import Axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { API_URL } from './constants';
import { auth0Exports } from './auth';

const SENTRY_IGNORED_STATUSES = [401, 403];

const authRequestInterceptor = async (request: InternalAxiosRequestConfig) => {
  await auth0Exports.waitUntilReady();

  if (auth0Exports.getIsAuthenticated()) {
    const getAccessTokenSilently = auth0Exports.getAccessTokenSilently();
    if (getAccessTokenSilently) {
      try {
        const token = await getAccessTokenSilently();
        if (token && request.headers) {
          request.headers.Authorization = `Bearer ${token}`;
        }
      } catch (e) {
        Sentry.captureException(e, {
          extra: {
            description: 'Silent token refresh failed before API request',
            url: request.url,
            method: request.method,
          },
        });
      }
    }
  }

  return request;
};

const sentryErrorInterceptor = (error: AxiosError) => {
  if (error.response?.status && !SENTRY_IGNORED_STATUSES.includes(error.response?.status)) {
    Sentry.captureException(error, {
      tags: {
        errorType: 'api_error',
        statusCode: error.response?.status,
      },
      extra: {
        url: error.config?.url,
        method: error.config?.method,
        responseData: error.response?.data,
      },
    });
  }

  return Promise.reject(error);
};

export const applyInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use(authRequestInterceptor);
  instance.interceptors.response.use((response) => response, sentryErrorInterceptor);
};

export const api = Axios.create({ baseURL: `${API_URL}` });
applyInterceptors(api);

type ApiErrorCode = {
  code: string;
  reasons: string[];
};

export const unpackApiErr = (err: Error | null): string | string[] => {
  if (err) {
    if ('response' in err) {
      const error = err.response as AxiosResponse;
      if (Array.isArray(error.data) && error.data.length > 0) {
        const apiErrors = error.data as ApiErrorCode[];
        return apiErrors.flatMap((e) => e.reasons);
      }
    }
  }
  return '';
};

export const getQueryParamsFromObj = (
  obj?: Record<string, string | number | boolean | undefined>,
) => {
  if (!obj) return '';

  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;

    if (Array.isArray(value) && value.length > 0) {
      value.forEach((e) => {
        searchParams.append(key, `${e}`);
      });
    } else {
      searchParams.append(key, `${value}`);
    }
  }

  return searchParams.toString();
};
